import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Award, Users2, Loader2, Trophy, Search, X, Download, FileText, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { computeRollingDaysRange } from "@/lib/exportDates";

/**
 * TechnicianStats — leaderboard aktivitas teknisi 7/30 hari terakhir.
 * Visible ONLY to admin & koordinator (parent must gate rendering).
 * Data source: GET /api/stats/technicians?days=7&wilayah=X
 */
export default function TechnicianStats() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [days, setDays] = useState(() => {
    const saved = Number(localStorage.getItem("ts_days"));
    return saved === 7 || saved === 30 ? saved : 7;
  });
  const [wilayah, setWilayah] = useState(() => localStorage.getItem("ts_wilayah") || "");
  const [wilayahList, setWilayahList] = useState([]);
  const [nameQuery, setNameQuery] = useState("");

  // Load wilayah list once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/tickets/wilayah-list");
        if (mounted) setWilayahList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[TechnicianStats] failed to load wilayah list:", err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErrored(false);
    (async () => {
      try {
        const params = { days };
        if (wilayah) params.wilayah = wilayah;
        const { data } = await api.get("/stats/technicians", { params });
        if (mounted) setRows(data.rows || []);
      } catch (err) {
        console.error("[TechnicianStats] failed to load:", err);
        if (mounted) setErrored(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [days, wilayah]);

  const changeDays = (n) => {
    setDays(n);
    localStorage.setItem("ts_days", String(n));
  };

  const changeWilayah = (w) => {
    setWilayah(w);
    if (w) localStorage.setItem("ts_wilayah", w);
    else localStorage.removeItem("ts_wilayah");
  };

  // Client-side name filter (case-insensitive substring)
  const filteredRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [rows, nameQuery]);

  const maxSelesai = filteredRows.reduce((m, r) => Math.max(m, r.selesai || 0), 0) || 1;

  // -------- Export dialog state (independent from dashboard days filter) --------
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#E5E7EB] rounded-xl shadow-[0_1px_2px_rgba(16,24,40,0.04)] overflow-hidden"
      data-testid="technician-stats-widget"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 sm:px-5 py-2.5 border-b border-[#E5E7EB]">
        <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h3 className="font-display text-sm sm:text-base font-bold tracking-tight flex items-center gap-1.5 whitespace-nowrap">
            <Trophy className="h-3.5 w-3.5 text-[#EA580C]" aria-label="Aktivitas Tim" />
            Statistik Teknisi — {days} Hari Terakhir
          </h3>
          <p className="text-[11px] text-[#6B7280] min-w-0 truncate">
            Klaim & Selesai per teknisi. Bukan KPI resmi — hanya monitoring aktivitas.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            role="group"
            aria-label="Rentang waktu"
            data-testid="ts-range-toggle"
            className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden text-[10px] font-bold uppercase tracking-widest"
          >
            {[7, 30].map((n) => {
              const active = days === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => changeDays(n)}
                  aria-pressed={active}
                  data-testid={`ts-range-${n}`}
                  className={`px-3 py-1 transition-colors ${
                    active
                      ? "bg-[#0A0A0A] text-white"
                      : "bg-white text-[#4B5563] hover:bg-[#F8F9FA]"
                  }`}
                >
                  {n} Hari
                </button>
              );
            })}
          </div>
          <span
            className="inline-flex items-center gap-1.5 bg-[#0055FF]/10 text-[#0055FF] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-[#0055FF]/20"
            data-testid="ts-scope-badge"
          >
            <Users2 className="h-3 w-3" /> Admin & Koordinator
          </span>
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            data-testid="ts-export-open"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#0A0A0A] bg-[#0A0A0A] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white transition hover:bg-[#111827] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]/40"
          >
            <Download className="h-3 w-3" /> Unduh Laporan
          </button>
        </div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        defaultWilayah={wilayah}
        defaultName={nameQuery}
        wilayahList={wilayahList}
      />

      {/* Filter row: name search + wilayah dropdown */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 px-4 sm:px-5 py-2 border-b border-[#E5E7EB] bg-[#F8F9FA]">
        <div className="relative flex-1 min-w-0">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
          <input
            type="text"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Cari nama teknisi…"
            data-testid="ts-name-filter"
            className="w-full h-8 pl-8 pr-8 rounded-md border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent"
          />
          {nameQuery && (
            <button
              type="button"
              onClick={() => setNameQuery("")}
              data-testid="ts-name-clear"
              aria-label="Bersihkan pencarian nama"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <select
          value={wilayah}
          onChange={(e) => changeWilayah(e.target.value)}
          data-testid="ts-wilayah-filter"
          className="h-8 rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent min-w-40"
        >
          <option value="">Semua Wilayah</option>
          {wilayahList.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="px-6 py-5 flex items-center justify-center text-sm text-[#9CA3AF] gap-2" data-testid="ts-loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          Memuat statistik…
        </div>
      )}

      {!loading && errored && (
        <div className="px-6 py-5 text-center text-sm text-[#991B1B]" data-testid="ts-error">
          Gagal memuat statistik teknisi. Coba muat ulang halaman.
        </div>
      )}

      {!loading && !errored && filteredRows.length === 0 && (
        <div className="px-4 sm:px-6 py-4 flex items-center justify-center gap-3 text-left" data-testid="ts-empty">
          <div className="h-9 w-9 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
            <Award className="h-4 w-4 text-[#9CA3AF]" />
          </div>
          <div className="min-w-0">
          <div className="font-display text-sm font-bold tracking-tight text-[#0A0A0A]">
            {rows.length === 0
              ? `Belum ada aktivitas dalam ${days} hari terakhir`
              : "Tidak ada teknisi yang cocok dengan filter"}
          </div>
          <div className="text-xs text-[#4B5563] mt-1">
            {rows.length === 0
              ? "Ketika teknisi klaim atau selesaikan tiket, ringkasannya akan muncul di sini."
              : "Ubah kata kunci nama atau wilayah untuk melihat data lain."}
          </div>
          </div>
        </div>
      )}

      {!loading && !errored && filteredRows.length > 0 && (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block overflow-auto max-h-[262px]" data-testid="ts-table-scroll">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] sticky top-0 z-10 bg-white shadow-[0_1px_0_#E5E7EB]">
                <tr>
                  <th className="px-6 py-2">#</th>
                  <th className="px-6 py-2">Nama Teknisi</th>
                  <th className="px-6 py-2 text-right">Klaim</th>
                  <th className="px-6 py-2 text-right">Selesai</th>
                  <th className="px-6 py-2 w-[34%]">Progres</th>
                </tr>
              </thead>
              <tbody data-testid="ts-table-body">
                {filteredRows.map((r, idx) => {
                  const pct = Math.min(100, Math.round(((r.selesai || 0) / maxSelesai) * 100));
                  const isTop = idx === 0 && (r.selesai || 0) > 0;
                  // Fase — Teknisi Drilldown. Klik row → /tickets pre-filtered
                  // by pic=<nama> + date range yang sedang dipilih (7/30 hari
                  // terakhir). Admin bisa langsung audit tiket per teknisi
                  // tanpa buka Manajemen Pekerjaan lalu filter manual.
                  const openDrilldown = () => {
                    const rng = computeRollingDaysRange(days);
                    const qs = new URLSearchParams({
                      pic: r.name,
                      date_from: rng.date_from,
                      date_to: rng.date_to,
                    });
                    navigate(`/tickets?${qs.toString()}`);
                  };
                  const onKey = (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDrilldown();
                    }
                  };
                  return (
                    <tr
                      key={r.name}
                      role="button"
                      tabIndex={0}
                      onClick={openDrilldown}
                      onKeyDown={onKey}
                      aria-label={`Lihat tiket teknisi ${r.name}`}
                      className="group border-t border-[#E5E7EB] hover:bg-[rgba(0,85,255,0.08)] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0055FF]"
                      data-testid={`ts-row-${idx}`}
                    >
                      <td className="px-6 py-2 font-mono-tab text-[#9CA3AF]">
                        {isTop ? <Trophy className="h-3.5 w-3.5 text-[#EA580C] inline" /> : `${idx + 1}`}
                      </td>
                      <td className="px-6 py-2 font-semibold text-[#0A0A0A] transition-colors group-hover:text-[#0055FF]">{r.name}</td>
                      <td className="px-6 py-2 text-right font-mono-tab">{r.claim}</td>
                      <td className="px-6 py-2 text-right">
                        <span className="font-mono-tab font-bold text-[#0A0A0A] transition-colors group-hover:text-[#0055FF]">{r.selesai}</span>
                      </td>
                      <td className="px-6 py-2" title="Selesai relatif terhadap teknisi dengan Selesai terbanyak">
                        <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden" data-testid={`ts-progress-${idx}`}>
                          <div
                            className="h-full rounded-full bg-[#065F46] transition-[width] duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <ul className="md:hidden divide-y divide-[#E5E7EB] max-h-[420px] overflow-y-auto" data-testid="ts-mobile-list">
            {filteredRows.map((r, idx) => {
              const isTop = idx === 0 && (r.selesai || 0) > 0;
              const pct = Math.min(100, Math.round(((r.selesai || 0) / maxSelesai) * 100));
              const openDrilldown = () => {
                const rng = computeRollingDaysRange(days);
                const qs = new URLSearchParams({
                  pic: r.name,
                  date_from: rng.date_from,
                  date_to: rng.date_to,
                });
                navigate(`/tickets?${qs.toString()}`);
              };
              const onKey = (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openDrilldown();
                }
              };
              return (
                <li
                  key={r.name}
                  role="button"
                  tabIndex={0}
                  onClick={openDrilldown}
                  onKeyDown={onKey}
                  aria-label={`Lihat tiket teknisi ${r.name}`}
                  className="px-6 py-4 cursor-pointer transition-all duration-100 hover:bg-[rgba(0,85,255,0.08)] active:scale-[0.985] active:bg-[rgba(0,85,255,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0055FF]"
                  data-testid={`ts-card-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {isTop && <Trophy className="h-3.5 w-3.5 text-[#EA580C] flex-shrink-0" />}
                        <span className="font-semibold text-[#0A0A0A] truncate">{r.name}</span>
                      </div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                        Rank #{idx + 1}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono-tab text-2xl font-black text-[#065F46] leading-none">{r.selesai}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mt-1">selesai</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full bg-[#065F46]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-[#4B5563]">
                    Klaim: <span className="font-mono-tab font-semibold text-[#0A0A0A]">{r.claim}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </motion.div>
  );
}


// ---------------------------------------------------------------------------
// Export dialog — periode laporan berdiri sendiri dari filter dashboard.
// ---------------------------------------------------------------------------
function fmtIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function fmtDisplay(iso) {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// Computes the [from, to] YYYY-MM-DD pair for a given preset. Exported so
// tests can assert the exact same math the UI uses. `today` is injected for
// deterministic testing; falls back to today's WIB-local date.
export function computeExportRange(preset, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  if (preset === "this_month") {
    return { from: fmtIso(new Date(y, m, 1)), to: fmtIso(new Date(y, m, d)) };
  }
  if (preset === "last_month") {
    const lastDay = new Date(y, m, 0).getDate();
    return {
      from: fmtIso(new Date(y, m - 1, 1)),
      to: fmtIso(new Date(y, m - 1, lastDay)),
    };
  }
  if (preset === "7d") {
    return { from: fmtIso(new Date(y, m, d - 6)), to: fmtIso(new Date(y, m, d)) };
  }
  if (preset === "30d") {
    return { from: fmtIso(new Date(y, m, d - 29)), to: fmtIso(new Date(y, m, d)) };
  }
  // "custom" — leave for caller to populate
  return { from: fmtIso(new Date(y, m, 1)), to: fmtIso(new Date(y, m, d)) };
}

function ExportDialog({ open, onOpenChange, defaultWilayah, defaultName, wilayahList }) {
  const [preset, setPreset] = useState("this_month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [wilayah, setWilayah] = useState(defaultWilayah || "");
  const [nameFilter, setNameFilter] = useState(defaultName || "");
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null

  // Sync dates when preset changes (Custom keeps user-edited values).
  useEffect(() => {
    if (!open) return;
    if (preset === "custom") return;
    const r = computeExportRange(preset);
    setDateFrom(r.from);
    setDateTo(r.to);
  }, [preset, open]);

  // On open, seed filters from dashboard state so the summary matches what
  // the user is looking at, but they remain editable.
  useEffect(() => {
    if (open) {
      setPreset("this_month");
      const r = computeExportRange("this_month");
      setDateFrom(r.from);
      setDateTo(r.to);
      setWilayah(defaultWilayah || "");
      setNameFilter(defaultName || "");
    }
  }, [open, defaultWilayah, defaultName]);

  const invalid = !dateFrom || !dateTo || dateFrom > dateTo;

  const download = async (fmt) => {
    if (invalid) {
      toast.error("Tanggal Mulai tidak boleh lebih besar dari Tanggal Akhir");
      return;
    }
    setBusy(fmt);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (wilayah) params.wilayah = wilayah;
      if (nameFilter.trim()) params.name = nameFilter.trim();
      const path = fmt === "pdf" ? "/stats/technicians/export.pdf" : "/stats/technicians/export.xlsx";
      const res = await api.get(path, { params, responseType: "blob" });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statistik_teknisi_${dateFrom}_${dateTo}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Laporan ${fmt.toUpperCase()} berhasil diunduh`);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = typeof j.detail === "string" ? j.detail : "Gagal mengunduh laporan";
        } catch (_) {
          msg = "Gagal mengunduh laporan";
        }
      } else if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = typeof j.detail === "string" ? j.detail : "Gagal mengunduh laporan";
        } catch (_) {
          msg = "Gagal mengunduh laporan";
        }
      } else {
        msg = err?.message || "Gagal mengunduh laporan";
      }
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ts-export-dialog">
        <DialogHeader>
          <DialogTitle>Unduh Laporan Statistik Teknisi</DialogTitle>
          <DialogDescription>
            Periode laporan berdiri sendiri dari filter 7/30 Hari di Dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1.5">Periode Cepat</div>
            <div className="grid grid-cols-3 gap-2" data-testid="ts-export-presets">
              {[
                { k: "this_month", label: "Bulan Ini" },
                { k: "last_month", label: "Bulan Lalu" },
                { k: "7d", label: "7 Hari" },
                { k: "30d", label: "30 Hari" },
                { k: "custom", label: "Custom" },
              ].map((p) => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => setPreset(p.k)}
                  aria-pressed={preset === p.k}
                  data-testid={`ts-export-preset-${p.k}`}
                  className={`h-8 rounded-md border text-[11px] font-bold uppercase tracking-wider transition ${
                    preset === p.k
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F8F9FA]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Tanggal Mulai</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
                data-testid="ts-export-date-from"
                className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Tanggal Akhir</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
                data-testid="ts-export-date-to"
                className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Wilayah</label>
              <select
                value={wilayah}
                onChange={(e) => setWilayah(e.target.value)}
                data-testid="ts-export-wilayah"
                className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
              >
                <option value="">Semua Wilayah</option>
                {(wilayahList || []).map((w) => (<option key={w} value={w}>{w}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Teknisi</label>
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Semua Teknisi"
                data-testid="ts-export-name"
                className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
              />
            </div>
          </div>

          <div className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded-md p-3 space-y-1" data-testid="ts-export-summary">
            <div><span className="text-[#9CA3AF]">Periode:</span> <b>{fmtDisplay(dateFrom)} — {fmtDisplay(dateTo)}</b></div>
            <div><span className="text-[#9CA3AF]">Wilayah:</span> <b>{wilayah || "Semua Wilayah"}</b></div>
            <div><span className="text-[#9CA3AF]">Teknisi:</span> <b>{nameFilter.trim() || "Semua Teknisi"}</b></div>
          </div>

          {invalid && (
            <div className="text-xs text-[#991B1B]" data-testid="ts-export-invalid">
              Tanggal Mulai tidak boleh lebih besar dari Tanggal Akhir.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="ts-export-cancel"
          >
            Batal
          </Button>
          <Button
            variant="outline"
            onClick={() => download("pdf")}
            disabled={invalid || busy !== null}
            data-testid="ts-export-pdf"
          >
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            <span className="ml-1.5">Unduh PDF</span>
          </Button>
          <Button
            onClick={() => download("xlsx")}
            disabled={invalid || busy !== null}
            data-testid="ts-export-xlsx"
          >
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span className="ml-1.5">Unduh Excel</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
